import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSocket } from '@/hooks/useSocket';
import { useTimeSync } from '@/hooks/useTimeSync';
import { useRoomStore } from '@/stores/roomStore';
import { useUserStore } from '@/stores/userStore';
import { YouTubePlayer, Controls, ProgressBar, VolumeSlider } from '@/components/player';
import { Participants, Queue, AddSong, ShareLink } from '@/components/room';
import { Button } from '@/components/Button';
import { Footer } from '@/components/Footer';
import { getSocket } from '@/lib/socket';

export default function Room() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { room, myRole } = useRoomStore();
  const { userName: displayUserName, isRegistered } = useUserStore();

  useEffect(() => {
    // Redirect to home if not registered (AuthGuard should handle this but just in case)
    if (!isRegistered || !roomId) {
      navigate('/');
      return;
    }
  }, [roomId, navigate, isRegistered]);

  // Initialize socket connection and time sync
  useSocket(roomId || '');
  useTimeSync();

  const handleLeaveRoom = () => {
    const socket = getSocket();
    socket.emit('room:leave');
    navigate('/');
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'owner': return 'badge-owner';
      case 'dj': return 'badge-dj';
      default: return 'badge-listener';
    }
  };

  if (!roomId) {
    return null;
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-card border-b border-default">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-lime-400 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-navy-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-primary">
                  {room?.name || 'Loading...'}
                </h1>
                <p className="text-sm text-secondary">Room: <span className="text-lime-400 font-mono">{roomId}</span></p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-primary">{displayUserName}</span>
                {myRole && (
                  <span className={`badge ${getRoleBadgeClass(myRole)}`}>{myRole}</span>
                )}
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={handleLeaveRoom}
              >
                Leave Room
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 lg:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Player & Queue */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Player */}
            <YouTubePlayer roomId={roomId} />

            {/* Player Controls */}
            <div className="card">
              <ProgressBar roomId={roomId} />
              <div className="flex items-center justify-between px-4 pb-4">
                <VolumeSlider />
                <Controls roomId={roomId} />
                <div className="w-32" /> {/* Spacer for symmetry */}
              </div>
            </div>

            {/* Queue */}
            <Queue roomId={roomId} />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Share Link */}
            <ShareLink roomId={roomId} />

            {/* Participants */}
            <Participants roomId={roomId} />

            {/* Add Song */}
            <AddSong roomId={roomId} />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 pb-6">
        <Footer />
      </footer>
    </div>
  );
}
